"use client";

import * as React from "react";
import { isNativePlatform } from "@/lib/capacitor/platform";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/app/_module/lib/utils";
import { useDeviceDetection } from "@/app/_module/hooks/useDeviceDetection";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// Helper function to get responsive Tailwind classes for max-width based on size
// Uses responsive breakpoints so server and client render the same initially
const getResponsiveMaxWidthClasses = (
  size: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full" = "lg"
): string => {
  // Responsive Tailwind classes that scale up on larger screens
  const sizeMap = {
    sm: "max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg",
    md: "max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl",
    lg: "max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl",
    xl: "max-w-md sm:max-w-lg md:max-w-2xl lg:max-w-3xl xl:max-w-4xl",
    "2xl": "max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-4xl xl:max-w-5xl",
    "3xl": "max-w-lg sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl",
    full: "max-w-[95vw] sm:max-w-[90vw] md:max-w-[85vw] lg:max-w-[80vw]",
  };

  return sizeMap[size];
};

// Legacy function - kept for backward compatibility but prefer getResponsiveMaxWidthClasses
const getResponsiveMaxWidth = (
  isMobile: boolean,
  isTablet: boolean,
  isLaptop: boolean,
  isDesktop: boolean,
  size: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full" = "lg"
): string => {
  // Size mappings for different devices - LARGER sizes for better desktop experience
  const sizeMap = {
    sm: {
      mobile: "max-w-xs",      // 320px
      tablet: "max-w-sm",      // 384px
      laptop: "max-w-md",      // 448px
      desktop: "max-w-lg",     // 512px
    },
    md: {
      mobile: "max-w-sm",      // 384px
      tablet: "max-w-md",      // 448px
      laptop: "max-w-lg",      // 512px
      desktop: "max-w-2xl",    // 672px
    },
    lg: {
      mobile: "max-w-sm",      // 384px
      tablet: "max-w-lg",      // 512px
      laptop: "max-w-2xl",     // 672px
      desktop: "max-w-3xl",    // 768px
    },
    xl: {
      mobile: "max-w-md",      // 448px
      tablet: "max-w-xl",      // 576px
      laptop: "max-w-3xl",     // 768px
      desktop: "max-w-4xl",    // 896px
    },
    "2xl": {
      mobile: "max-w-md",      // 448px
      tablet: "max-w-2xl",     // 672px
      laptop: "max-w-4xl",     // 896px
      desktop: "max-w-5xl",    // 1024px
    },
    "3xl": {
      mobile: "max-w-lg",      // 512px
      tablet: "max-w-3xl",     // 768px
      laptop: "max-w-5xl",     // 1024px
      desktop: "max-w-6xl",    // 1152px
    },
    full: {
      mobile: "max-w-[95vw]",
      tablet: "max-w-[90vw]",
      laptop: "max-w-[85vw]",
      desktop: "max-w-[80vw]",
    },
  };

  const sizes = sizeMap[size];
  if (isMobile) return sizes.mobile;
  if (isTablet) return sizes.tablet;
  if (isLaptop) return sizes.laptop;
  if (isDesktop) return sizes.desktop;
  return sizes.laptop; // fallback
};

// Standard DialogContent - uses responsive Tailwind classes for proper sizing on all devices
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    {/* .app-dialog-wrap: inert empty div on desktop (zero size, no styles).
        In the native shell, globals.css turns it into a fixed flex overlay
        that centers the content — WKWebView refuses to shrink-wrap an
        inset-positioned box (height:fit-content AND max-content both resolve
        to full stretch, sim-verified), so small dialogs ballooned full-height
        in the app. Flex centering shrink-wraps reliably. */}
    <div className="app-dialog-wrap">
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Base styles
        // [transform:translate(-50%,-50%)], NOT translate-x/y utilities: Tailwind v4's
        // translate-* set the separate `translate` property, and lightningcss in prod
        // builds folds the native-app shell's `translate: none` neutralizer into
        // `transform` — leaving the utility live to COMPOSE and slide the dialog half
        // off-screen in the iOS app only (dev builds don't merge, so they hide it).
        "app-dialog-content fixed left-[50%] top-[50%] z-50 grid w-[95vw] [transform:translate(-50%,-50%)] gap-4 border bg-background p-4 md:p-6 shadow-lg duration-200",
        // Animations
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        // Layout
        "rounded-lg max-h-[90vh] max-h-[90dvh] overflow-y-auto",
        // Responsive max-width: scales up on larger screens
        "max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
    </div>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

// Responsive DialogContent - uses responsive Tailwind classes based on size prop
// Avoids hydration issues by using CSS breakpoints instead of JS device detection
interface ResponsiveDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full";
  overlayClassName?: string;
  overlayStyle?: React.CSSProperties;
}

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ResponsiveDialogContentProps
>(({ className, children, size = "lg", overlayClassName, overlayStyle, ...props }, ref) => {
  // Use responsive Tailwind classes instead of device detection
  const responsiveMaxWidth = getResponsiveMaxWidthClasses(size);

  // (Saga-era inline bottom-sheet styles REMOVED 2026-08-26. They existed
  // because the class-based overrides "didn't land" — root cause turned out
  // to be a global fixed->sticky rewrite rule, since deleted. The leftover
  // inline zIndex pinned the OVERLAY at 99998 above the content, washing
  // every dialog gray in the shell. globals.css owns shell dialog geometry.)

  return (
    <DialogPortal>
      <DialogOverlay
        className={overlayClassName}
        style={overlayStyle}
      />
      {/* shell flex-centering wrapper — see DialogContent note */}
      <div className="app-dialog-wrap">
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Base styles
          "app-dialog-content fixed left-[50%] top-[50%] z-50 grid w-[95vw] [transform:translate(-50%,-50%)] gap-4 border bg-background p-4 md:p-6 shadow-lg duration-200",
          // Animations
          "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          // Layout
          "rounded-lg max-h-[90vh] max-h-[90dvh] overflow-y-auto",
          // Responsive max-width based on size prop
          responsiveMaxWidth,
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-4 top-4 z-20 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
});
ResponsiveDialogContent.displayName = "ResponsiveDialogContent";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  ResponsiveDialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  getResponsiveMaxWidth,
  getResponsiveMaxWidthClasses,
};
